"use client";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Check, MoveRight, Star, Zap } from "lucide-react";
import { useEffect, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useSession } from "next-auth/react";

export default function CreditPricing({ packages = [] }) {
  const { data: session } = useSession();
  const [loading, setLoading] = useState(null);
  const [isHeaderVisible, setIsHeaderVisible] = useState(false);
  const [visibleCards, setVisibleCards] = useState(new Set());

  // Animation effects
  useEffect(() => {
    // Header animation
    const headerTimer = setTimeout(() => {
      setIsHeaderVisible(true);
    }, 200);

    // Staggered card animations
    packages.forEach((_, index) => {
      const cardTimer = setTimeout(() => {
        setVisibleCards(prev => new Set([...prev, index]));
      }, 500 + (index * 100)); // Start after header, then stagger by 100ms
    });

    return () => {
      clearTimeout(headerTimer);
    };
  }, [packages]);

  const handlePurchaseCredits = async (packageId) => {
    if (!session?.user) {
      // Redirect to sign in
      window.location.href = '/sign-in';
      return;
    }

    setLoading(packageId);
    try {
      const response = await fetch("/api/credits/purchase", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ packageId }),
      });

      const data = await response.json();
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else {
        throw new Error(data.error || 'Failed to create checkout');
      }
    } catch (error) {
      console.error("Purchase error:", error);
      alert("There was an error starting your purchase. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="w-full py-10 lg:py-20 bg-gradient-to-b from-slate-50 to-white dark:from-slate-950 dark:to-slate-900">
      <div className="container mx-auto px-6">
        <div className={`flex text-center justify-center items-center gap-6 flex-col transition-all duration-800 ease-out ${
          isHeaderVisible
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-6'
        }`}>
          <Badge variant="outline" className="text-orange-600 dark:text-orange-400 border-orange-500/50 dark:border-orange-500/30 bg-orange-50 dark:bg-orange-900/20 px-4 py-2 text-sm font-semibold">
            Pay As You Go. Credits Never Expire
          </Badge>
          <div className="flex gap-4 flex-col">
            <h1 className="text-4xl md:text-5xl lg:text-6xl tracking-tight max-w-4xl text-center font-bold text-gray-900 dark:text-white">
              Simple,{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-amber-500">Pay-As-You-Go</span>{" "}
              Pricing
            </h1>
            <p className="text-lg md:text-xl leading-relaxed tracking-tight text-gray-600 dark:text-slate-400 max-w-3xl text-center mx-auto">
              No subscriptions. No commitments. Credits never expire. <span className="font-semibold text-gray-900 dark:text-white">Company research is always free</span> — only prospect analyses use credits.
            </p>
            <p className="text-sm font-medium text-gray-500 dark:text-slate-400 max-w-3xl text-center mx-auto">
              1 credit = 1 prospect. Every prospect gets unlimited call briefs.
            </p>
          </div>
        </div>

        <div className="grid pt-16 lg:pt-20 text-left grid-cols-1 md:grid-cols-2 lg:grid-cols-3 w-full gap-6 auto-rows-fr">
          {packages.map((pack, index) => (
            <Card
              key={pack.id}
              className={cn(
                "relative w-full rounded-2xl border flex flex-col h-full transition-all duration-700 ease-out",
                // Base styling
                "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700",
                // Animation states
                visibleCards.has(index)
                  ? 'opacity-100 translate-y-0 hover:shadow-lg hover:border-slate-300 dark:hover:border-slate-600'
                  : 'opacity-0 translate-y-8',
                // Popular Pack - the ONLY highlighted card
                pack.popular && "!border-orange-500 !bg-gradient-to-br !from-orange-50 !to-amber-50 dark:!from-slate-800 dark:!to-slate-800 shadow-xl shadow-orange-500/10 lg:scale-105",
                // Best Value - subtle highlight, not competing with Popular
                pack.bestValue && "!border-slate-300 dark:!border-slate-600",
                // Best value subtle highlight
                pack.bestValue && !pack.popular && "hover:!border-orange-300 dark:hover:!border-orange-700"
              )}
            >
              {pack.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-gradient-to-r from-orange-500 to-amber-500 text-white px-3 py-1 shadow-lg shadow-orange-500/25">
                    <Star className="w-3 h-3 mr-1" />
                    Most Popular
                  </Badge>
                </div>
              )}

              {pack.bestValue && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-slate-700 dark:bg-slate-600 text-white px-3 py-1">
                    <Zap className="w-3 h-3 mr-1" />
                    Best Value
                  </Badge>
                </div>
              )}


              <CardHeader className="space-y-2 pb-4">
                <CardTitle>
                  <span className={cn(
                    "flex flex-row gap-4 items-center font-semibold text-xl text-gray-900 dark:text-white",
                    pack.popular && "!text-orange-700 dark:!text-orange-400"
                  )}>
                    {pack.title}
                  </span>
                </CardTitle>
                <CardDescription className="text-sm font-medium text-gray-600 dark:text-slate-400">
                  {pack.description}
                </CardDescription>
              </CardHeader>

              <CardContent className="flex-1">
                <div className="flex flex-col gap-6 justify-start h-full">
                  {/* Pricing */}
                  <div className="space-y-1">
                    <p className="flex flex-row items-baseline gap-2">
                      <span className={cn(
                        "text-4xl font-bold text-gray-900 dark:text-white",
                        pack.popular && "!text-orange-600 dark:!text-orange-400"
                      )}>
                        ${pack.price}
                      </span>
                      <span className="text-sm font-medium text-gray-600 dark:text-slate-400">
                        for {pack.credits} {pack.kind === 'import' ? `enrichment${pack.credits !== 1 ? 's' : ''}` : `prospect${pack.credits !== 1 ? 's' : ''}`}
                      </span>
                    </p>
                    <p className="text-sm text-gray-500 dark:text-slate-400">
                      {pack.pricePerCredit} per {pack.kind === 'import' ? 'enrichment' : 'credit'}
                    </p>
                    {pack.savings && (
                      <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                        {pack.savings}
                      </p>
                    )}
                  </div>

                  {/* Features */}
                  <div className="flex flex-col gap-3 justify-start flex-1">
                    {pack.features.map((feature, index) => (
                      <div key={index} className="flex flex-row gap-3">
                        <Check className={cn(
                          "w-4 h-4 mt-0.5 shrink-0 text-slate-400 dark:text-slate-500",
                          pack.popular && "!text-orange-500 dark:!text-orange-400"
                        )} />
                        <p className="text-sm text-gray-700 dark:text-slate-300">{feature}</p>
                      </div>
                    ))}
                  </div>

                  {/* Purchase Button */}
                  <Button
                    variant="outline"
                    size="lg"
                    className={cn(
                      "gap-2 w-full font-semibold mt-auto rounded-xl transition-all duration-300",
                      // Popular Pack - the ONLY highlighted button
                      pack.popular && "!bg-gradient-to-r !from-orange-500 !to-amber-500 hover:!from-orange-600 hover:!to-amber-600 !text-white !border-0 shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40",
                      // All other packs - outlined style
                      !pack.popular && "!bg-transparent !border-slate-300 dark:!border-slate-600 !text-slate-700 dark:!text-slate-300 hover:!bg-slate-100 dark:hover:!bg-slate-700 hover:!border-slate-400 dark:hover:!border-slate-500"
                    )}
                    onClick={() => handlePurchaseCredits(pack.id)}
                    disabled={loading === pack.id}
                  >
                    {loading === pack.id ? "Processing..." : pack.kind === 'import' ? "Get Import Credits" : "Get Credits"}
                    {loading !== pack.id && <MoveRight className="w-4 h-4" />}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className={`mt-16 space-y-8 transition-all duration-800 ease-out delay-1000 ${
          visibleCards.size === packages.length
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-4'
        }`}>
          <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-600 dark:text-slate-400">
            <div className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 rounded-full shadow-sm border border-slate-200 dark:border-slate-700">
              <svg className="w-5 h-5 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="font-medium text-slate-700 dark:text-slate-300">Secure Stripe payments</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 rounded-full shadow-sm border border-slate-200 dark:border-slate-700">
              <svg className="w-5 h-5 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
              </svg>
              <span className="font-medium text-slate-700 dark:text-slate-300">Credits never expire</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 rounded-full shadow-sm border border-slate-200 dark:border-slate-700">
              <svg className="w-5 h-5 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <span className="font-medium text-slate-700 dark:text-slate-300">Cancel anytime</span>
            </div>
          </div>

          <div className="relative max-w-3xl mx-auto overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 p-8 text-center">
            {/* Background decoration */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-gradient-to-br from-orange-500/20 to-amber-500/10 rounded-full blur-2xl" />
              <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-gradient-to-br from-blue-600/10 to-cyan-500/5 rounded-full blur-2xl" />
            </div>
            <div className="relative z-10">
              <p className="text-lg font-semibold text-white mb-2">
                Start with 5 free prospects when you sign up
              </p>
              <p className="text-sm text-slate-400">
                Try Sabine and see how AI-powered preparation saves you time on every call
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
